// Budget Engine — pure computation.
//
// Core principle: everything is a line. A category total is ALWAYS the sum of
// its resolved lines; there is no separately-stored category total.
//
// A month is computed on demand from two inputs:
//   1. Versioned template lines (the source of truth).
//   2. Per-month override diffs that ride on top.
// Nothing computed is ever stored. Past months stay frozen because template
// edits create a new version effective from the current month forward — an
// older month resolves the version that was in force then.
//
// This module is IO-free and deterministic: no `Date.now()`, no reads/writes.
// Persistence + versioning writes live in the component layer.

// The first month this engine owns. Months before it are served by the legacy
// `budgets` table (frozen), never by the engine.
export const BUDGET_CUTOVER_MONTH = '2026-07';

export type Frequency =
  | 'monthly'
  | 'per_named_day'
  | 'specific_months'
  | 'quarterly'
  | 'per_day'
  | 'per_need';

// Selectable in the UI, in display order: Specific day(s) / Specific month(s) /
// As needed. `monthly`, `per_day`, and `quarterly` are intentionally omitted —
// "every month" is Specific month(s) with all months, "every day" is Specific
// day(s) with all days — but the type + evaluator still handle those values for
// any pre-existing rows.
export const FREQUENCIES: Frequency[] = [
  'per_named_day', 'specific_months', 'per_need',
];

// Quarterly is a preset of specific_months. Default anchor Jan/Apr/Jul/Oct.
export const QUARTERLY_MONTHS = [1, 4, 7, 10];

export interface FreqConfig {
  weekday?: number;    // legacy single day (0=Sun .. 6=Sat), for per_named_day
  weekdays?: number[]; // one or more days (0=Sun .. 6=Sat), for per_named_day
  months?: number[];   // 1..12, for specific_months / quarterly
}

// One stored version of a template line. A line's identity is `lineGroupId`;
// editing/deleting inserts a new version with a later `effectiveFrom`.
export interface TemplateLineVersion {
  id: string;
  lineGroupId: string;
  categoryId: string;
  name: string;
  amount: number;
  frequency: Frequency;
  freqConfig?: FreqConfig | null;
  templateNote?: string | null;
  effectiveFrom: string; // 'YYYY-MM'
  isDeleted: boolean;    // this version marks the line removed going forward
  sortOrder: number;
  deletedAt?: string | null;
}

// One per-month diff. Two shapes discriminated by `lineGroupId`:
//   * lineGroupId set  -> a diff on a template line (edit-lock / delete-lock / note)
//   * lineGroupId null -> a standalone line (added one-off, or "adjust category")
export interface Override {
  id: string;
  month: string;               // 'YYYY-MM'
  lineGroupId?: string | null;
  lockedAmount?: number | null;
  deletedThisMonth?: boolean;
  categoryId?: string | null;
  name?: string | null;
  addedAmount?: number | null;
  isAdjustment?: boolean;
  monthNote?: string | null;
  sortOrder?: number;
  deletedAt?: string | null;
}

export type LineSource = 'template' | 'added' | 'adjustment';

export interface ResolvedLine {
  key: string;                   // stable React key
  lineGroupId: string | null;    // null for added/adjustment lines
  overrideId: string | null;     // the diff row backing this line, if any
  categoryId: string;
  name: string;
  frequency: Frequency | null;   // null for added/adjustment lines
  freqConfig: FreqConfig | null;
  rawAmount: number;             // unrounded computed amount for the month
  amount: number;                // rounded to nearest 10 (display + total)
  source: LineSource;
  locked: boolean;               // amount is pinned by an override (edit-lock)
  deletedThisMonth: boolean;     // template line struck out for this month
  templateNote: string | null;
  monthNote: string | null;
  // ordering hints (template sort_order / override sort_order) used only by the
  // internal line sorter; not meaningful to consumers.
  templateOrder?: number;
  overrideOrder?: number;
}

export interface CategoryResult {
  categoryId: string;
  lines: ResolvedLine[];
  total: number;                 // sum of rounded line amounts
}

export interface MonthResult {
  month: string;
  categories: CategoryResult[];              // only categories with >=1 line
  byCategoryId: Record<string, CategoryResult>;
  total: number;
}

// ============ rounding ============

// Every displayed amount rounds to the nearest 10 AED (no decimals). Negatives
// (adjustment lines) round symmetrically.
export const round10 = (n: number): number => Math.round((Number(n) || 0) / 10) * 10;

// ============ calendar helpers ============

const parseMonth = (m: string): { year: number; month1: number } => {
  const [y, mo] = m.split('-').map(Number);
  return { year: y, month1: mo };
};

// Days in a calendar month. month1 is 1..12.
export const daysInMonth = (year: number, month1: number): number =>
  new Date(Date.UTC(year, month1, 0)).getUTCDate();

// Count of a given weekday (0=Sun..6=Sat) within a strict calendar month.
export const countWeekdayInMonth = (year: number, month1: number, weekday: number): number => {
  const total = daysInMonth(year, month1);
  let count = 0;
  for (let d = 1; d <= total; d++) {
    if (new Date(Date.UTC(year, month1 - 1, d)).getUTCDay() === weekday) count++;
  }
  return count;
};

// ============ frequency evaluation ============

// The unrounded amount a template-line version contributes to a given month.
export const evaluateFrequency = (line: TemplateLineVersion, month: string): number => {
  const { year, month1 } = parseMonth(month);
  const cfg = line.freqConfig || {};
  switch (line.frequency) {
    case 'monthly':
      return line.amount;
    case 'per_day':
      return line.amount * daysInMonth(year, month1);
    case 'per_named_day': {
      // Sum the occurrences of every selected weekday in the month, then × amount.
      // Supports multi-day (weekdays) and the legacy single (weekday).
      const days = (cfg.weekdays && cfg.weekdays.length)
        ? cfg.weekdays
        : (typeof cfg.weekday === 'number' ? [cfg.weekday] : []);
      let count = 0;
      for (const wd of days) count += countWeekdayInMonth(year, month1, wd);
      return line.amount * count;
    }
    case 'specific_months':
    case 'quarterly': {
      const months = cfg.months && cfg.months.length ? cfg.months : (line.frequency === 'quarterly' ? QUARTERLY_MONTHS : []);
      return months.includes(month1) ? line.amount : 0;
    }
    case 'per_need':
      // Placeholder — contributes nothing until the user fills it in per month
      // via an edit override.
      return 0;
    default:
      return 0;
  }
};

// ============ template version resolution ============

// For a month, return the active line definition per lineGroupId: the newest
// live version with effectiveFrom <= month. A version marked isDeleted removes
// the line for that month (returned so callers can render it struck / omit it).
export interface ResolvedTemplateDef {
  def: TemplateLineVersion;
  isDeleted: boolean;
}

export const resolveTemplateForMonth = (
  versions: TemplateLineVersion[],
  month: string,
): Map<string, ResolvedTemplateDef> => {
  const byGroup = new Map<string, TemplateLineVersion>();
  for (const v of versions) {
    if (v.deletedAt) continue;
    if (v.effectiveFrom > month) continue; // not yet in force
    const cur = byGroup.get(v.lineGroupId);
    // Pick the greatest effectiveFrom; tie-break by created ordering via id is
    // unnecessary because at most one version per (group, month) is written.
    if (!cur || v.effectiveFrom > cur.effectiveFrom) byGroup.set(v.lineGroupId, v);
  }
  const out = new Map<string, ResolvedTemplateDef>();
  for (const [group, def] of byGroup) {
    out.set(group, { def, isDeleted: !!def.isDeleted });
  }
  return out;
};

// ============ month computation ============

const sourceRank: Record<LineSource, number> = { template: 0, added: 1, adjustment: 2 };

const sortLines = (a: ResolvedLine, b: ResolvedLine): number => {
  if (sourceRank[a.source] !== sourceRank[b.source]) return sourceRank[a.source] - sourceRank[b.source];
  const so = (a.overrideOrder ?? a.templateOrder ?? 0) - (b.overrideOrder ?? b.templateOrder ?? 0);
  if (so !== 0) return so;
  return (a.name || '').localeCompare(b.name || '');
};

// Compute a full month: resolve template lines, apply per-month overrides, round,
// and group into category totals. `month` is 'YYYY-MM'.
export const computeMonth = (
  month: string,
  versions: TemplateLineVersion[],
  overrides: Override[],
): MonthResult => {
  const liveOverrides = overrides.filter(o => !o.deletedAt && o.month === month);
  // Line diffs, keyed by lineGroupId (at most one live per line per month).
  const lineDiffs = new Map<string, Override>();
  const standalones: Override[] = [];
  for (const o of liveOverrides) {
    if (o.lineGroupId) lineDiffs.set(o.lineGroupId, o);
    else standalones.push(o);
  }

  const resolvedTemplate = resolveTemplateForMonth(versions, month);
  const lines: ResolvedLine[] = [];

  for (const [group, { def, isDeleted }] of resolvedTemplate) {
    if (isDeleted) continue; // template removed this line going forward
    const diff = lineDiffs.get(group);
    const deletedThisMonth = !!diff?.deletedThisMonth;
    const hasLock = diff != null && diff.lockedAmount != null;
    const rawAmount = deletedThisMonth
      ? 0
      : hasLock
        ? Number(diff!.lockedAmount)
        : evaluateFrequency(def, month);
    lines.push({
      key: `t:${group}`,
      lineGroupId: group,
      overrideId: diff?.id ?? null,
      categoryId: def.categoryId,
      name: def.name,
      frequency: def.frequency,
      freqConfig: def.freqConfig || null,
      rawAmount,
      amount: deletedThisMonth ? 0 : round10(rawAmount),
      source: 'template',
      locked: hasLock || deletedThisMonth,
      deletedThisMonth,
      templateNote: def.templateNote ?? null,
      monthNote: diff?.monthNote ?? null,
      templateOrder: def.sortOrder,
    });
  }

  for (const o of standalones) {
    const raw = Number(o.addedAmount) || 0;
    lines.push({
      key: `o:${o.id}`,
      lineGroupId: null,
      overrideId: o.id,
      categoryId: o.categoryId as string,
      name: o.name || (o.isAdjustment ? 'Manual adjustment' : 'One-off'),
      frequency: null,
      freqConfig: null,
      rawAmount: raw,
      amount: round10(raw),
      source: o.isAdjustment ? 'adjustment' : 'added',
      locked: false,
      deletedThisMonth: false,
      templateNote: null,
      monthNote: o.monthNote ?? null,
      overrideOrder: o.sortOrder,
    });
  }

  lines.sort(sortLines);

  const byCategoryId: Record<string, CategoryResult> = {};
  for (const line of lines) {
    let cat = byCategoryId[line.categoryId];
    if (!cat) { cat = { categoryId: line.categoryId, lines: [], total: 0 }; byCategoryId[line.categoryId] = cat; }
    cat.lines.push(line);
    cat.total += line.amount; // total = sum of rounded lines, by definition
  }

  const categories = Object.values(byCategoryId);
  const total = categories.reduce((s, c) => s + c.total, 0);
  return { month, categories, byCategoryId, total };
};

// ============ reconciliation ============

export type ReconKind = 'edited' | 'deleted';

export interface ReconTarget {
  month: string;
  kind: ReconKind;      // the month's diff is an amount-lock (edited) or delete-lock (deleted)
  override: Override;
  lockedAmount: number | null;
}

// When a template line is edited or deleted, find the locked per-month diffs
// (current month forward) that need a Keep/Update decision. Added and adjustment
// lines never appear — they ride on top automatically. Past months are excluded:
// they resolve an older template version and are frozen.
export const findReconciliationTargets = (
  lineGroupId: string,
  fromMonth: string,
  overrides: Override[],
): ReconTarget[] => {
  const targets: ReconTarget[] = [];
  for (const o of overrides) {
    if (o.deletedAt) continue;
    if (o.lineGroupId !== lineGroupId) continue;
    if (o.month < fromMonth) continue;
    const isDelete = !!o.deletedThisMonth;
    const isEdit = !isDelete && o.lockedAmount != null;
    if (!isEdit && !isDelete) continue; // note-only diffs aren't locks
    targets.push({
      month: o.month,
      kind: isDelete ? 'deleted' : 'edited',
      override: o,
      lockedAmount: o.lockedAmount ?? null,
    });
  }
  targets.sort((a, b) => a.month.localeCompare(b.month));
  return targets;
};

// ============ versioning decision ============

// Given the currently-active version for a line and the current month, decide
// whether an edit should update that row in place (it already starts this month,
// so no older month depends on it) or insert a fresh version effective this
// month (preserving the older version for past months). Pure decision; the
// caller performs the write.
export const shouldInsertNewVersion = (
  activeVersion: TemplateLineVersion | null | undefined,
  currentMonth: string,
): boolean => {
  if (!activeVersion) return true;
  return activeVersion.effectiveFrom < currentMonth;
};
